/**
 * @description Utilities class for working with encryption/decryption
 */
import { isEmpty } from "@utils/var.check";
import appConfigs from "@configs/app";
const cryptoEnabled = window.crypto && window.crypto.subtle && appConfigs.cryptoEnabled;
//server public key
let publicKey = "";
//client key pair
let keyPair = {};

const CryptoUtils = {
  /**
   * generate client side key pair for sending to server
   * @returns {jQuery|*}
   */
  generateKeyPair: function () {
    return new Promise((resolve, reject) => {
      if (!this.enabled || !isEmpty(keyPair)) {
        return resolve(keyPair);
      }

      window.crypto.subtle
        .generateKey(
          {
            name: "RSA-OAEP",
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-256",
          },
          true,
          ["encrypt", "decrypt"],
        )
        .then(
          function (newKeyPair) {
            keyPair = newKeyPair;
            resolve(newKeyPair);
          }.bind(this),
          function (reason) {
            console.error(reason);
            reject(reason);
          },
        );
    });
  },

  /**
   * export client side public key
   * @param key
   * @returns {jQuery|*}
   */
  exportPublicKey: function (key) {
    return new Promise((resolve, reject) => {
      if (!this.enabled || !key) {
        return resolve(key);
      }

      window.crypto.subtle.exportKey("spki", key).then(
        function (exported) {
          const exportedAsString = this.ab2str(exported);
          resolve(window.btoa(exportedAsString));
        }.bind(this),
        function (reason) {
          console.error(reason);
          reject(reason);
        },
      );
    });
  },
  /**
   * import server side public key
   * @param key
   * @returns {jQuery|*}
   */
  importPublicKey: function (key) {
    return new Promise((resolve, reject) => {
      if (!cryptoEnabled || !key) {
        return resolve();
      }
      // base64 decode the string to get the binary data
      const binaryString = window.atob(key);
      // convert from a binary string to an ArrayBuffer
      const arrayBuffer = this.str2ab(binaryString);
      window.crypto.subtle
        .importKey(
          "spki",
          arrayBuffer,
          {
            name: "RSA-OAEP",
            hash: "SHA-256",
          },
          true,
          ["encrypt"],
        )
        .then(
          function (key) {
            publicKey = key;
            resolve();
          }.bind(this),
          function (reason) {
            console.error(reason);
            reject(reason);
          },
        );
    });
  },
  /**
   *   Convert an ArrayBuffer into a string
   *   from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
   * @param buf
   * @returns {string}
   */
  ab2str: function (buf) {
    return String.fromCharCode.apply(null, new Uint8Array(buf));
  },
  /**
   *   Convert a string into an ArrayBuffer
   *   from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
   * @param str
   * @returns {ArrayBuffer}
   */
  str2ab: function (str) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
  },
  /**
   * Convert a string into a byte array
   * Same functionality as TextEncoder
   * @param str
   * @returns {Uint8Array}
   */
  str2ba: function (str) {
    const ba = new Uint8Array(str.length);

    for (let i = 0; i < str.length; i += 1) {
      ba[i] = str.charCodeAt(i);
    }

    return ba;
  },
  /**
   * encrypt using server side public key
   * @param message
   * @returns {jQuery|*}
   */
  encrypt: function (message: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!cryptoEnabled || !message || !publicKey) {
        return resolve(message);
      }

      const encoded = this.str2ba(message);

      window.crypto.subtle
        .encrypt(
          {
            name: "RSA-OAEP",
            //MS Edge need this specific field in encryption(error 80700011)
            hash: { name: "SHA-256" },
          },
          publicKey,
          encoded,
        )
        .then(
          function (ciphertext) {
            // convert from an ArrayBuffer to a binary string
            const binaryString = this.ab2str(ciphertext);
            // base64 encode the string
            resolve(window.btoa(binaryString));
          }.bind(this),
          function (reason) {
            console.error(reason);
            reject(reason);
          },
        );
    });
  },
  hash: async function (message: string, length = 32): Promise<string> {
    if (!cryptoEnabled) {
      return message;
    }

    const textEncoder = new TextEncoder();
    const data = textEncoder.encode(message);

    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .substring(0, length);
  },
};

export default CryptoUtils;
